import React, { FC, useEffect, useState } from "react";
import styled from "@emotion/styled";
import { LinearProgress } from "@mui/material";
import { t } from "i18next";
import ErrorIcon from "@mui/icons-material/Error";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import StyledIconButton from "@components/StyledIconButton";
import ClearIcon from "@mui/icons-material/Clear";

const Container = styled.div`
  width: 100%;
  position: relative;
`;

const Image = styled.div`
  width: 100%;
  height: 80px;
  box-sizing: border-box;
  background-repeat: no-repeat;
  background-size: cover;
  background-position: top;
  position: relative;
  border-radius: 4px;
  overflow: hidden;
`;

const Footer = styled.div`
  margin-top: 8px;
`;

const Status = styled.div`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  margin-top: 8px;
  font-size: 14px;
`;

const ImageIconContainer = styled.div`
  background: rgba(232, 222, 248, 0.8);
  width: 24px;
  height: 24px;
  position: absolute;
  bottom: 4px;
  right: 4px;
  border-radius: 4px;
`;

const RemoveButton = styled(StyledIconButton)`
  position: absolute;
  top: 0;
  right: 0;
  transform: translate(50%, -50%);
  z-index: 1;
  border: solid 1px;
  width: 20px;
  height: 20px;
  display: flex;
  justify-content: center;
  align-items: center;
`;

export interface FileUploadProps {
  file: File | string;
  status: "Done" | "Uploading" | "Error" | null;
  progress?: number;
  onRemove?: () => void;
}

const FileUpload: FC<FileUploadProps> = ({
  file,
  status,
  progress,
  onRemove,
}) => {
  const [url, setUrl] = useState<string | undefined>();
  const style = {
    backgroundImage: `url('${url || process.env.PUBLIC_URL + "/images/file-placeholder.png"}')`,
  };

  useEffect(() => {
    if (typeof file === "string") {
      setUrl(file);
      return;
    }
    const reader = new FileReader();
    reader.onloadend = function () {
      setUrl(reader.result as string);
    };
    reader.readAsDataURL(file);
  }, [file]);

  return (
    <Container>
      {onRemove && (
        <RemoveButton
          size="small"
          color="inherit"
          onClick={(e) => {
            e.stopPropagation();
            onRemove();
          }}
          bgColor="#fff"
        >
          <ClearIcon fontSize="small" />
        </RemoveButton>
      )}
      <Image style={style}>
        {(status === "Done" || status === "Error") && (
          <ImageIconContainer>
            {status === "Done" ? (
              <CheckCircleIcon color="success" />
            ) : (
              <ErrorIcon color="error" />
            )}
          </ImageIconContainer>
        )}
      </Image>
      <Footer>
        {status === "Uploading" && (
          <>
            <LinearProgress />
            <Status> {t(`picturesUpload.status.uploading`)}</Status>
          </>
        )}
      </Footer>
    </Container>
  );
};

export default FileUpload;
